import { createFileRoute, Outlet, useChildMatches, useNavigate } from '@tanstack/react-router'
import { GroupsScreen } from '@/features/groups/groups'
import { type GroupsSearch, groupsSearchSchema } from '@/features/groups/search'

/**
 * `/groups` is the list; `/groups/$groupId` is one group's detail
 * (plans/58 D-17).
 *
 * A parent route whose component never renders `<Outlet />` silently swallows
 * its children — the URL matches, and the parent's own screen shows instead.
 * Rather than splitting this file into an index sibling (which would rename a
 * tracked file), it does what `apps/company`'s `accounting/journal.tsx` does:
 * render the list only when no child has matched, and let the router fill in
 * the detail below otherwise.
 *
 * `validateSearch` is what puts the status filter in the URL: a filtered view
 * is linkable and survives a reload, and the back button steps through filter
 * states. Per the route-shell rule, this file holds route definitions and
 * renders; the screens live in `src/features/groups/`.
 */
export const Route = createFileRoute('/_authenticated/groups')({
  validateSearch: groupsSearchSchema,
  component: GroupsRoute,
})

function GroupsRoute() {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const children = useChildMatches()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <GroupsScreen
      search={search}
      onSearchChange={(patch: Partial<GroupsSearch>) => {
        void navigate({
          to: '/groups',
          search: { ...search, ...patch },
          replace: true,
        })
      }}
    />
  )
}
